import { Box, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { DataGrid as MuiDataGrid } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import { useGoToScrollPosition } from "../datagrid/useGoToScrollPosition";
import { useStore } from "../../store/store";
import { saveScrollPosition } from "../datagrid/utils";
import { columns } from "./columnsDefinition";
import { useEffect, useState } from "react";
import { TreatmentProvidedAppointment, useTreatmentsProvidedAppointments } from "../../api/useTreatmentsProvidedAppointments";
import { useUser } from "../../api/useAuth";

type AppointmentFulfilledMode = "all" | "notFulfilled";
type AppointmentListMode = "all_cases" | "gc_cases" | "therapist_cases";

type TreatmentsProvidedAppointmentsProps = {
  heightOfHeaders: number;
  header: React.ReactNode;
};

export const TreatmentsProvidedAppointments = ({ heightOfHeaders, header }: TreatmentsProvidedAppointmentsProps) => {
  const navigate = useNavigate();
  const { data, error } = useTreatmentsProvidedAppointments();
  const { data: user } = useUser();

  const { apiRef } = useGoToScrollPosition("treatments", data);
  const { gridSettings, gridSettings: { paginationModel } } = useStore();

  const [filterAppointmentFulfillment, setFilterAppointmentFulfillment] = useState<Array<TreatmentProvidedAppointment>>(data ?? []);

  const [appointmentFulfilledMode, setAppointmentFulfilledMode] = useState<AppointmentFulfilledMode>("all");
  const [appointmentList, setAppointmentList] = useState<AppointmentListMode>("all_cases");

  useEffect(() => {
    if (!Array.isArray(data)) return;

    let filteredData = data;

    if (appointmentFulfilledMode === "notFulfilled") {
      filteredData = filteredData.filter((row) =>
        Object.values(row.appointment_fulfillment ?? {}).some(
          (item) => item.booked_sessions < item.prescribed_quantity
        )
      );
    }

    if (appointmentList === "gc_cases") {
      filteredData = filteredData.filter((row) => row.client_owner === user?.id);
    }

    if (appointmentList === "therapist_cases") {
      filteredData = filteredData.filter((row) => row.therapists_involved_in_treamtent?.includes(user?.id ?? ""));
    }

    setFilterAppointmentFulfillment(filteredData);
  }, [data, appointmentFulfilledMode, appointmentList, user?.id]);


  if (!data || error) return null;
  return (
    <Box display="flex" flexDirection="column">
      {header}
      <AppointmentFulfillmentToggle
        onChangeAppointmentFulfillment={(value) => setAppointmentFulfilledMode(value)}
        valueAppointmentFulfillment={appointmentFulfilledMode}
        valueAppointmentList={appointmentList}
        onChangeAppointmentList={(value) => setAppointmentList(value)} />

      <Box height={`calc(100vh - ${heightOfHeaders}px)`} sx={{ backgroundColor: "#fff" }}>
        <MuiDataGrid
          apiRef={apiRef}
          rows={filterAppointmentFulfillment}
          columns={columns}
          paginationModel={paginationModel}
          pageSizeOptions={[paginationModel?.pageSize || 100]}
          onCellClick={(params) => {
            navigate(`/${"treatments"}/${params.id}/detail`);
            saveScrollPosition(gridSettings, apiRef, params, "treatments");
          }}
          onPaginationModelChange={(paginationModel) => {
            gridSettings.setGridSettings({
              paginationModel: paginationModel,
              scrollPosition: {
                top: 0,
                left: 0,
              },
            });
          }}
          disableColumnFilter
          disableColumnSelector
          disableDensitySelector
        />
      </Box>
    </Box>
  );
};

const AppointmentFulfillmentToggle = ({
  onChangeAppointmentFulfillment,
  valueAppointmentFulfillment,
  valueAppointmentList,
  onChangeAppointmentList,
}: {
  valueAppointmentFulfillment: AppointmentFulfilledMode;
  onChangeAppointmentFulfillment: (value: AppointmentFulfilledMode) => void;
  valueAppointmentList: AppointmentListMode;
  onChangeAppointmentList: (value: AppointmentListMode) => void;
}) => {
  const MIN_WIDTH = 220;
  return (
    <Box>
      <ToggleButtonGroup
        value={valueAppointmentFulfillment}
        exclusive
        onChange={(_, newValue) => {
          if (newValue !== null) onChangeAppointmentFulfillment(newValue);
        }}
        color="secondary"
        sx={{ mb: 2, backgroundColor: "white", mr: 4 }}
      >
        <ToggleButton sx={{ minWidth: MIN_WIDTH }} value="notFulfilled">Agendamentos Não Cumpridos Atual</ToggleButton>
        <ToggleButton sx={{ minWidth: MIN_WIDTH }} value="all">Todos os Agendamentos</ToggleButton>
      </ToggleButtonGroup>
      <ToggleButtonGroup
        value={valueAppointmentList}
        exclusive
        onChange={(_, newValue) => {
          if (newValue !== null) onChangeAppointmentList(newValue);
        }}
        color="secondary"
        sx={{ mb: 2, backgroundColor: "white" }}
      >
        <ToggleButton sx={{ minWidth: MIN_WIDTH }} value="all_cases">Todos os Casos</ToggleButton>
        <ToggleButton sx={{ minWidth: MIN_WIDTH }} value="gc_cases">Casos de GC</ToggleButton>
        <ToggleButton sx={{ minWidth: MIN_WIDTH }} value="therapist_cases">Casos de Terapeuta</ToggleButton>
      </ToggleButtonGroup>
    </Box>
  );
};
